import useConfirmSyncEnergyPerformanceCertificate from '@/api/mutations/energyPerformanceCertificate/useConfirmSyncEnergyPerformanceCertificate';
import useSyncEnergyPerformanceCertificate, {
  SyncEnergyPerformanceCertificateConfirmationNeededResponse,
} from '@/api/mutations/energyPerformanceCertificate/useSyncEnergyPerformanceCertificate';
import {
  Alert,
  Badge,
  Button,
  Card,
  CardButton,
  Modal,
  useToastContext,
} from '@payaca/ambrosia';
import { FC, useState } from 'react';

type EpcPromptStepProps = {
  addressId: string;
  projectId: string;
  onSkip: () => void;
  onEpcLinked: () => void;
};

export const EpcPromptStep: FC<EpcPromptStepProps> = (props) => {
  const { addressId, projectId, onSkip, onEpcLinked } = props;
  const { pushSuccess, pushInfo, pushError } = useToastContext();

  const {
    mutateAsync: syncEpc,
    isPending: isSyncingEpc,
    data: syncData,
  } = useSyncEnergyPerformanceCertificate({
    projectId,
    options: { meta: { skipGlobalErrorToast: true } },
  });

  const [showConfirmation, setShowConfirmation] = useState(false);

  const handleSearchEpc = () => {
    if (isSyncingEpc) return;
    void syncEpc({ addressId })
      .then((response) => {
        switch (response.syncEnergyPerformanceCertificate.__typename) {
          case 'SyncEnergyPerformanceCertificateFoundResponse': {
            pushSuccess('EPC linked successfully');
            onEpcLinked();
            break;
          }
          case 'SyncEnergyPerformanceCertificateUnchangedResponse': {
            pushInfo('EPC already up to date');
            onEpcLinked();
            break;
          }
          case 'SyncEnergyPerformanceCertificateConfirmationNeededResponse': {
            setShowConfirmation(true);
            break;
          }
          case 'SyncEnergyPerformanceCertificateErrorResponse': {
            pushError(
              response.syncEnergyPerformanceCertificate.message ||
                'Could not find an EPC for this address'
            );
            break;
          }
        }
      })
      .catch(() => {
        pushError('Something went wrong searching for an EPC');
      });
  };

  const confirmationCertificates =
    syncData?.syncEnergyPerformanceCertificate.__typename ===
    'SyncEnergyPerformanceCertificateConfirmationNeededResponse'
      ? syncData.syncEnergyPerformanceCertificate.certificates
      : null;

  return (
    <>
      <Modal.Body>
        {!showConfirmation ? (
          <>
            <Alert colour="blue" className="mb-4">
              Would you like to link an EPC to this project before continuing?
              This can pre-fill property information for the Spruce submission.
            </Alert>
            <div className="flex gap-2">
              <Button
                onClick={handleSearchEpc}
                isProcessing={isSyncingEpc}
                disabled={isSyncingEpc}
              >
                Search for EPC
              </Button>
              <Button variant="secondary" onClick={onSkip}>
                Continue without EPC
              </Button>
            </div>
          </>
        ) : (
          <>
            {confirmationCertificates && (
              <EpcConfirmationInline
                certificates={confirmationCertificates}
                addressId={addressId}
                projectId={projectId}
                onConfirmed={onEpcLinked}
              />
            )}
          </>
        )}
      </Modal.Body>
    </>
  );
};

type EpcConfirmationInlineProps = {
  certificates: SyncEnergyPerformanceCertificateConfirmationNeededResponse['certificates'];
  addressId: string;
  projectId: string;
  onConfirmed: () => void;
};

const EpcConfirmationInline: FC<EpcConfirmationInlineProps> = (props) => {
  const { certificates, addressId, projectId, onConfirmed } = props;
  const { pushSuccess, pushError } = useToastContext();

  const { mutateAsync: confirmSync, isPending } =
    useConfirmSyncEnergyPerformanceCertificate({
      projectId,
      options: { meta: { skipGlobalErrorToast: true } },
    });

  const handleSelect = (certificateNumber: string) => {
    if (isPending) return;
    void confirmSync({ addressId, certificateNumber })
      .then(() => {
        pushSuccess('EPC linked successfully');
        onConfirmed();
      })
      .catch(() => {
        pushError('Something went wrong');
      });
  };

  return (
    <>
      <Alert colour="yellow" className="mb-4">
        Multiple energy performance certificates found. Please select the
        correct one.
      </Alert>
      <ul className="m-0 list-none space-y-4 p-0">
        {certificates.map((cert) => {
          const { certificateNumber } = cert.metadata;
          if (!certificateNumber) return null;
          return (
            <li key={certificateNumber}>
              <CardButton onClick={() => handleSelect(certificateNumber)}>
                <Card.Body>
                  <div className="flex flex-row gap-4">
                    <div className="flex flex-col gap-2">
                      <Badge
                        colour="green"
                        variant="solid"
                        className="flex h-12 w-12 items-center justify-center text-xl font-bold"
                      >
                        {cert.energyPerformance.currentEnergyRating}
                      </Badge>
                    </div>
                    <div>
                      <p>
                        {cert.property.address}, {cert.property.postcode}
                      </p>
                      <p>
                        {cert.property.propertyType} ({cert.property.builtForm})
                      </p>
                      <p>UPRN: {cert.property.uprn}</p>
                    </div>
                  </div>
                </Card.Body>
              </CardButton>
            </li>
          );
        })}
      </ul>
    </>
  );
};
